import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';

export interface Props {
  title: string;
  columnHeader: string;
  breakdown: Array<{ value: string; count: string; percent: string }>;
  skipFormatting?: boolean;
}

const AGE_RANGE_REGEX = /^AGE_(\d+)_(\d+)$/;

export function formatAgeValue(value: string): string | null {
  const upper = value.trim().toUpperCase();
  const rangeMatch = upper.match(AGE_RANGE_REGEX);
  if (rangeMatch) {
    return `${rangeMatch[1]} - ${rangeMatch[2]} years old`;
  }
  if (upper === 'AGE_80_PLUS') {
    return '80+ years old';
  }
  return null;
}

/** Returns sort key for AGE_X_Y and AGE_80_PLUS so age bands order correctly; null otherwise. */
export function getAgeBandSortKey(value: string): number | null {
  const upper = value.trim().toUpperCase();
  const rangeMatch = upper.match(AGE_RANGE_REGEX);
  if (rangeMatch) {
    return Number(rangeMatch[1]);
  }
  if (upper === 'AGE_80_PLUS') {
    return 80;
  }
  return null;
}

export function formatCellValue(value: string): string {
  const ageFormatted = formatAgeValue(value);
  if (ageFormatted) return ageFormatted;

  const words = value.replace(/_/g, ' ').toLowerCase().split(/\s+/);
  const smallWords = new Set(['or', 'of', 'and', 'the']);
  return words
    .map((word, index) =>
      smallWords.has(word) && index > 0 && index < words.length - 1
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

export function isSuppressed(cellValue: string): boolean {
  const normalized = cellValue.trim().toLowerCase();
  return (
    normalized === '' ||
    normalized === '-' ||
    normalized === '—' ||
    normalized.includes('<11') ||
    normalized.includes('suppressed')
  );
}

const NORMALIZE_DASH = /[\u2013\u2014]/g;

export function normalizeCountString(count: string): string {
  return count.trim().replace(NORMALIZE_DASH, '-');
}

type PillVariant = 'yellow' | 'green' | null;

export function getPillVariant(count: string): PillVariant {
  if (isSuppressed(count)) return null;
  const normalizedCount = normalizeCountString(count);
  const yellowCounts = ['11-25'];
  const greenCounts = [
    '26-50',
    '51-100',
    '101-250',
    '251-500',
    '501-1000',
    '>1000',
  ];
  if (yellowCounts.some((range) => normalizedCount.includes(range)))
    return 'yellow';
  if (greenCounts.some((range) => normalizedCount.includes(range)))
    return 'green';
  return null;
}

const BreakdownTableCard = ({
  title,
  columnHeader,
  breakdown,
  skipFormatting = false,
}: Props) => {
  if (!breakdown || breakdown.length === 0) {
    return null;
  }

  const sortedBreakdown = [...breakdown].sort((rowA, rowB) => {
    const ageKeyA = getAgeBandSortKey(rowA.value);
    const ageKeyB = getAgeBandSortKey(rowB.value);
    if (ageKeyA !== null && ageKeyB !== null) {
      return ageKeyA - ageKeyB;
    }

    const valueA = Number(rowA.value);
    const valueB = Number(rowB.value);
    const aIsNumeric = !Number.isNaN(valueA);
    const bIsNumeric = !Number.isNaN(valueB);
    if (aIsNumeric && bIsNumeric) {
      return valueA - valueB;
    }

    return rowA.value.localeCompare(rowB.value, undefined, {
      sensitivity: 'base',
    });
  });

  return (
    <Card>
      <TableHeader>
        <Title>{title}</Title>
        <CategoryCount>
          {breakdown.length}{' '}
          {breakdown.length === 1 ? 'category' : 'categories'}
        </CategoryCount>
      </TableHeader>
      <TableContainer sx={{ pb: 10 }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 'auto' }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 110 }} />
          </colgroup>
          <TableHead>
            <HeaderRow>
              <HeaderCell>{columnHeader}</HeaderCell>
              <HeaderCell align="center">COUNT</HeaderCell>
              <HeaderCell align="center">%</HeaderCell>
            </HeaderRow>
          </TableHead>
          <TableBody>
            {sortedBreakdown.map((dimensionRow) => (
              <DataRow key={dimensionRow.value}>
                <DataCell>
                  {skipFormatting
                    ? dimensionRow.value
                    : formatCellValue(dimensionRow.value)}
                </DataCell>
                <DataCell align="center">
                  <RangeCell
                    $suppressed={isSuppressed(dimensionRow.count)}
                    $pillVariant={getPillVariant(dimensionRow.count)}
                  >
                    {dimensionRow.count}
                  </RangeCell>
                </DataCell>
                <DataCell
                  align="center"
                  $suppressed={isSuppressed(dimensionRow.percent)}
                >
                  {dimensionRow.percent}
                </DataCell>
              </DataRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default BreakdownTableCard;

const Card = styled(Box)(() => ({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.custom.cohortCardBorder}`,
  borderRadius: 10,
}));

const TableHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(4),
}));

const Title = styled(Typography)(() => ({
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
}));

const CategoryCount = styled(Typography)(() => ({
  fontSize: 12,
  fontWeight: 700,
  color: theme.palette.custom.cohortMetaText,
  paddingRight: theme.spacing(2),
}));

const HeaderRow = styled(TableRow)(() => ({
  backgroundColor: theme.palette.custom.cohortTableHeaderBg,
}));

const HeaderCell = styled(TableCell)(() => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.custom.cohortColumnHeader,
  textTransform: 'uppercase',
  borderTop: `1px solid ${theme.palette.custom.cohortCardBorder}`,
  borderBottom: `1px solid ${theme.palette.custom.cohortCardBorder}`,
}));

const DataRow = styled(TableRow)(() => ({
  borderBottom: `1px solid ${theme.palette.custom.cohortCardBorder}`,
}));

const DataCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== '$suppressed',
})<{ $suppressed?: boolean }>(({ $suppressed }) => ({
  fontSize: 14,
  color: $suppressed
    ? theme.palette.custom.suppressedText
    : theme.palette.custom.darkBrown,
  borderBottom: `1px solid ${theme.palette.custom.cohortCardBorder}`,
}));

const RangeCell = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== '$suppressed' && prop !== '$pillVariant',
})<{
  $suppressed?: boolean;
  $pillVariant?: PillVariant;
}>(({ $suppressed, $pillVariant }) => {
  const base = {
    fontSize: 14,
    fontWeight: 700,
    width: 70,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1, 1),
    borderRadius: 4,
  };
  if ($suppressed) {
    return {
      ...base,
      color: theme.palette.custom.suppressedText,
      fontWeight: 500,
    };
  }
  if ($pillVariant === 'yellow') {
    return {
      ...base,
      backgroundColor: theme.palette.custom.rangePillYellow,
      color: theme.palette.custom.darkBrown,
    };
  }
  if ($pillVariant === 'green') {
    return {
      ...base,
      backgroundColor: theme.palette.custom.rangePillGreen,
      color: theme.palette.custom.darkBrown,
    };
  }
  return { ...base, color: theme.palette.custom.darkBrown };
});
